import { useReadContracts } from 'wagmi';
import { erc20Abi } from 'viem';
import { useMemo } from 'react';
import { DEFAULT_WAGMI_QUERY_ERC20_INFO_STALE_TIME } from '@/onchain/querySettings';

export const useToken = (token: `0x${string}`) => {
  const result = useReadContracts({
    contracts: [
      {
        address: token,
        abi: erc20Abi,
        functionName: 'decimals',
      },
      {
        address: token,
        abi: erc20Abi,
        functionName: 'name',
      },
      {
        address: token,
        abi: erc20Abi,
        functionName: 'symbol',
      },
      {
        address: token,
        abi: erc20Abi,
        functionName: 'totalSupply',
      },
    ],
    query: {
      staleTime: DEFAULT_WAGMI_QUERY_ERC20_INFO_STALE_TIME,
      gcTime: DEFAULT_WAGMI_QUERY_ERC20_INFO_STALE_TIME * 2,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  });
  return {
    decimals: result.data?.[0].result,
    name: result.data?.[1].result,
    symbol: result.data?.[2].result,
    totalSupply: result.data?.[3].result,
    isLoading: result.isLoading,
    isError: result.isError,
    error: result.error,
  };
};

const TOKEN_CONTRACT_FUNCTION_NAMES = ['decimals', 'name', 'symbol', 'totalSupply'];

export function useTokens(tokens: `0x${string}`[]) {
  const params = useMemo(() => {
    const tokenContracts = tokens.map((token) =>
      TOKEN_CONTRACT_FUNCTION_NAMES.map((functionName) => ({
        address: token,
        abi: erc20Abi,
        functionName,
      })),
    );
    const contracts = tokenContracts.flat();
    return {
      contracts,
      query: {
        staleTime: DEFAULT_WAGMI_QUERY_ERC20_INFO_STALE_TIME,
        gcTime: DEFAULT_WAGMI_QUERY_ERC20_INFO_STALE_TIME * 2,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
      },
    };
  }, [tokens]);

  const { data, isLoading, isError, error } = useReadContracts(params);

  const tokensDetails = useMemo(() => {
    if (!data) return [];

    return tokens.map((token, index) => {
      // This index represents the index of the first function name for the token
      const startIdx = index * TOKEN_CONTRACT_FUNCTION_NAMES.length;
      return {
        address: token,
        decimals: data[startIdx]?.result,
        name: data[startIdx + 1]?.result,
        symbol: data[startIdx + 2]?.result,
        totalSupply: data[startIdx + 3]?.result,
      };
    });
  }, [data, tokens]);

  return {
    tokensDetails,
    isLoading,
    isError,
    error,
  };
}
